import { forwardRef, type ButtonHTMLAttributes } from "react";
import { cn } from "@/lib/utils";
import { Spinner } from "./primitives";

type Variant = "primary" | "secondary" | "ghost" | "danger" | "link";
type Size = "sm" | "md" | "lg" | "xl" | "icon" | "icon-sm";

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
}

const variants: Record<Variant, string> = {
  // Primário = a cor de destaque (reservada para a ação principal, ao vivo e não lidos), plano.
  primary:
    "border border-transparent bg-accent font-semibold text-accent-fg shadow-sm hover:bg-accent-strong active:bg-accent/85",
  secondary:
    "border border-border-strong bg-surface text-fg shadow-[0_1px_1px_hsl(var(--shadow-color)/0.06)] hover:bg-bg-muted active:bg-bg-emphasis",
  ghost: "border border-transparent text-fg-muted hover:bg-bg-muted hover:text-fg active:bg-bg-emphasis",
  // Ações destrutivas: a única cor dos botões, com significado.
  danger:
    "border border-[color-mix(in_oklab,var(--danger)_45%,transparent)] bg-transparent text-[var(--danger)] hover:bg-[color-mix(in_oklab,var(--danger)_12%,transparent)]",
  link: "border border-transparent px-0 text-fg underline-offset-4 hover:underline",
};

const sizes: Record<Size, string> = {
  sm: "h-7 rounded-md px-2.5 text-[12px] gap-1.5",
  md: "h-8 rounded-md px-3 text-[13px] gap-1.5",
  lg: "h-9 rounded-md px-4 text-[13.5px] gap-2",
  xl: "h-11 rounded-lg px-5 text-[14px] gap-2",
  icon: "h-8 w-8 rounded-md justify-center",
  "icon-sm": "h-6 w-6 rounded-[5px] justify-center",
};

const base =
  "inline-flex select-none items-center justify-center whitespace-nowrap font-medium outline-none transition-[background-color,color,border-color,opacity] duration-[120ms] focus-visible:ring-2 focus-visible:ring-ring/60 focus-visible:ring-offset-1 focus-visible:ring-offset-bg";

/** Classes do botão para outros elementos (ex.: <Link> com cara de botão). */
export function buttonVariants({
  variant = "secondary",
  size = "md",
  className,
}: { variant?: Variant; size?: Size; className?: string } = {}) {
  return cn(base, variants[variant], sizes[size], className);
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { className, variant = "secondary", size = "md", loading, disabled, children, ...props },
  ref,
) {
  return (
    <button
      ref={ref}
      className={cn(
        base,
        "disabled:pointer-events-none disabled:opacity-40",
        variants[variant],
        sizes[size],
        className,
      )}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...props}
    >
      {loading ? <Spinner className="h-3.5 w-3.5" /> : null}
      {children}
    </button>
  );
});
